import type {RepositoryPickerCurrentRepoQuery} from '@github-ui/item-picker/repository'
import {Portal} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useCallback, useRef} from 'react'
import type {PreloadedQuery} from 'react-relay'
import type {OnCreateProps} from '..'
import {
  useIssueCreateContext,
  useSafeClose,
  DisplayMode,
  CreateIssueFooter,
  CreateIssueWithPreselectedRepo,
  CreateIssue,
} from '..'
import type {CreateIssueSharedProps} from '../CreateIssue'
import type {IssueCreateUrlParams} from '../utils/template-args'
import {CreateIssueDialogHeader} from './CreateIssueDialogHeader'

export type CreateIssueDialogProps = {
  currentRepoQueryRef?: PreloadedQuery<RepositoryPickerCurrentRepoQuery> | null
  navigate: (url: string) => void
} & CreateIssueSharedProps

export const CreateIssueDialog = ({
  currentRepoQueryRef,
  onCreateSuccess,
  onCancel,
  navigate,
  ...props
}: CreateIssueDialogProps): JSX.Element => {
  const {storageKeyPrefix, resetDisplayMode} = useIssueCreateContext()

  // These two callbacks wrap the passed in callbacks to ensure that the display mode is reset
  // The reason why it's done here is we have access to the create context safely.
  const resetDisplayModeOnClose = useCallback(() => {
    onCancel()
    resetDisplayMode()
  }, [onCancel, resetDisplayMode])

  const resetDisplayModeOnCreateSuccess = useCallback(
    ({issue, createMore}: OnCreateProps) => {
      onCreateSuccess({issue, createMore})
      if (!createMore) {
        resetDisplayMode()
      }
    },
    [onCreateSuccess, resetDisplayMode],
  )

  const {onSafeClose} = useSafeClose({storageKeyPrefix, onCancel: resetDisplayModeOnClose})
  const createIssueUrlParamsRef = useRef<IssueCreateUrlParams>(null)
  const {displayMode} = useIssueCreateContext()

  const sharedCreateIssueProps = {
    storageKeyPrefix,
    onCreateSuccess: resetDisplayModeOnCreateSuccess,
    onCancel: onSafeClose,
    createIssueUrlParamsRef,
    insidePortal: true,
    ...props,
  }

  return (
    <Portal>
      <Dialog
        renderHeader={headerProps => (
          <CreateIssueDialogHeader
            showFullScreenButton={props.optionConfig.showFullScreenButton}
            createIssueUrlParamsRef={createIssueUrlParamsRef}
            navigate={navigate}
            {...headerProps}
          />
        )}
        renderFooter={() => (
          <Dialog.Footer sx={{display: displayMode === DisplayMode.IssueCreation ? 'flex' : 'none'}}>
            <CreateIssueFooter onClose={() => onSafeClose()} />
          </Dialog.Footer>
        )}
        onClose={onSafeClose}
        width="xlarge"
        height="auto"
        sx={{
          width: '100%',
          margin: 4,
          maxWidth: '800px',
          maxHeight: 'clamp(300px, 80vh, 800px)',
        }}
      >
        {currentRepoQueryRef ? (
          <CreateIssueWithPreselectedRepo currentRepoQueryRef={currentRepoQueryRef} {...sharedCreateIssueProps} />
        ) : (
          <CreateIssue {...sharedCreateIssueProps} />
        )}
      </Dialog>
    </Portal>
  )
}

try{ CreateIssueDialog.displayName ||= 'CreateIssueDialog' } catch {}